import {
  normalizeReactionBuckets,
} from './messageReactionModel';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import {
  JOURNEY_PUBLICATION,
  JOURNEY_PUBLICATION_PREVIEW,
  journeyPublicationIdOf,
} from './journeyPublicationCardModel';

export const CUSTOMER_SERVICE = 'CUSTOMER_SERVICE';
export const DIRECT = 'DIRECT';

export function asId(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function conversationIdOf(row) {
  return asId(row?.conversationId ?? row?.id);
}

export function createClientMessageId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function isCustomerServiceConversation(type) {
  return String(type || '') === CUSTOMER_SERVICE;
}

export function previewOf(message) {
  if (!message) return '';
  if (message.status === 'REVOKED' || message.revokedAt) return '[已撤回]';
  switch (message.messageType) {
    case 'TEXT':
      return message.text || '';
    case 'IMAGE':
      return '[圖片]';
    case 'STICKER':
      return '[貼圖]';
    case 'GIF':
      return '[GIF]';
    case 'AUDIO':
      return '[語音]';
    case 'VIDEO':
      return '[影片]';
    case 'FILE':
      return message.fileName || '[檔案]';
    case 'LOCATION':
      return message.locationName || '[位置]';
    case 'DECISION_CARD':
      return message.decisionCard?.title || message.text || '[決策]';
    case 'SITUATION_CARD':
      return message.situationCard?.title || message.text || '[旅程狀況]';
    case 'BUSINESS_CARD':
      return message.businessCard?.fallbackText || message.text || '[卡片]';
    case JOURNEY_PUBLICATION:
      return message.text || JOURNEY_PUBLICATION_PREVIEW;
    default:
      return message.text || message.lastMessagePreview || '[訊息]';
  }
}

export function formatChatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function normalizeConversation(row) {
  const conversationId = conversationIdOf(row);
  const unreadCount = Number(row?.unreadCount) || 0;
  const lastMessageSeq = row?.lastMessageSeq == null ? null : Number(row.lastMessageSeq);
  return {
    ...row,
    conversationId,
    id: conversationId,
    conversationType: row?.conversationType || '',
    peerMemberId: row?.peerMemberId == null ? null : asId(row.peerMemberId),
    participantMemberIds: Array.isArray(row?.participantMemberIds)
      ? row.participantMemberIds.map(asId)
      : [],
    title: row?.title || '',
    lastMessagePreview: row?.lastMessagePreview || '',
    lastMessageAt: row?.lastMessageAt || row?.lastMessageTime || null,
    lastMessageSeq,
    unreadCount,
    muteMode: row?.muteMode || 'OFF',
    muted: Boolean(row?.muted),
    myLastReadSeq: row?.myLastReadSeq == null ? 0 : Number(row.myLastReadSeq),
  };
}

export function inboxConversations(list) {
  return (Array.isArray(list) ? list : [])
    .map(normalizeConversation)
    .filter((row) => row.conversationId && !isCustomerServiceConversation(row.conversationType));
}

export function normalizeMessage(row) {
  const conversationId = conversationIdOf(row);
  const { journeyPublication: _persistedSnapshot, ...rest } = row || {};
  return {
    ...rest,
    journeyPublicationId: journeyPublicationIdOf(row),
    conversationId,
    serverMessageId: row?.serverMessageId == null ? '' : String(row.serverMessageId),
    clientMessageId: row?.clientMessageId == null ? '' : String(row.clientMessageId),
    senderMemberId: row?.senderMemberId == null ? null : asId(row.senderMemberId),
    seq: row?.seq == null ? null : Number(row.seq),
    text: row?.text || '',
    messageType: row?.messageType || 'TEXT',
    status: row?.status || '',
    objectKey: row?.objectKey == null || row?.objectKey === '' ? '' : String(row.objectKey),
    mediaUrl: row?.mediaUrl == null || row?.mediaUrl === '' ? '' : String(row.mediaUrl),
    fileName: row?.fileName == null || row?.fileName === '' ? '' : String(row.fileName),
    mimeType: row?.mimeType == null || row?.mimeType === '' ? '' : String(row.mimeType),
    reactions: normalizeReactionBuckets(row?.reactions),
    decisionCard: row?.decisionCard
      ? {
          ...row.decisionCard,
          decisionId: asId(row.decisionCard.decisionId),
          journeyId: asId(row.decisionCard.journeyId),
          selectedOptionId: row.decisionCard.selectedOptionId == null
            ? null
            : asId(row.decisionCard.selectedOptionId),
        }
      : row?.decisionCard ?? null,
    situationCard: row?.situationCard
      ? {
          ...row.situationCard,
          situationId: asId(row.situationCard.situationId),
          journeyId: asId(row.situationCard.journeyId),
          assignedToMemberId: row.situationCard.assignedToMemberId == null
            ? ''
            : asId(row.situationCard.assignedToMemberId),
        }
      : row?.situationCard ?? null,
    businessCard: row?.businessCard
      ? {
          ...row.businessCard,
          rendererKey: String(row.businessCard.rendererKey || ''),
          cardSchemaVersion: row.businessCard.cardSchemaVersion == null
            ? null
            : Number(row.businessCard.cardSchemaVersion),
          fallbackText: String(row.businessCard.fallbackText || ''),
          deepLink: String(row.businessCard.deepLink || ''),
          payload: normalizeBusinessCardPayload(row.businessCard.payload),
        }
      : row?.businessCard ?? null,
  };
}

export function normalizeMessagePage(page) {
  const items = (page?.items || []).map(normalizeMessage);
  return {
    items,
    hasMoreBefore: Boolean(page?.hasMoreBefore),
    hasMoreAfter: Boolean(page?.hasMoreAfter),
    oldestSeq: page?.oldestSeq == null ? null : Number(page.oldestSeq),
    newestSeq: page?.newestSeq == null ? null : Number(page.newestSeq),
  };
}

function messageKey(message) {
  if (message?.serverMessageId) return `s:${message.serverMessageId}`;
  if (message?.clientMessageId) return `c:${message.clientMessageId}`;
  if (message?.seq != null) return `q:${message.seq}`;
  return '';
}

function normalizeBusinessCardPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload ?? null;
  const next = { ...payload };
  ['activityId', 'journeyId', 'journeyDayId'].forEach((key) => {
    if (next[key] != null && next[key] !== '') next[key] = asId(next[key]);
  });
  return next;
}

function isRevoked(message) {
  return String(message?.status || '').toUpperCase() === 'REVOKED' || Boolean(message?.revokedAt);
}

export function mergeMessages(existing, incoming) {
  const byKey = new Map();
  [...(existing || []), ...(incoming || [])].forEach((raw) => {
    const incomingHasReactions = Object.prototype.hasOwnProperty.call(raw || {}, 'reactions');
    const message = normalizeMessage(raw);
    const key = messageKey(message);
    if (!key) return;
    const previous = byKey.get(key);
    if (!previous) {
      byKey.set(key, message);
      return;
    }
    if (isRevoked(previous) && !isRevoked(message)) {
      byKey.set(key, previous);
      return;
    }
    const next = { ...previous, ...message };
    if (isRevoked(message)) {
      next.reactions = incomingHasReactions ? message.reactions : [];
    } else if (!incomingHasReactions) {
      next.reactions = previous.reactions;
    }
    byKey.set(key, next);
  });
  return [...byKey.values()].sort((a, b) => {
    const sa = a.seq == null ? Number.MAX_SAFE_INTEGER : a.seq;
    const sb = b.seq == null ? Number.MAX_SAFE_INTEGER : b.seq;
    if (sa !== sb) return sa - sb;
    return String(a.clientMessageId || '').localeCompare(String(b.clientMessageId || ''));
  });
}

export function applyMessageEditedToInbox(conversations, message) {
  const incoming = normalizeMessage(message);
  if (!incoming.conversationId || isCustomerServiceConversation(incoming.conversationType)) {
    return conversations;
  }
  return conversations.map((row) => {
    if (row.conversationId !== incoming.conversationId) return row;
    const isLatest =
      row.lastMessageSeq != null && incoming.seq != null && Number(row.lastMessageSeq) === Number(incoming.seq);
    if (!isLatest) return row;
    return {
      ...row,
      lastMessagePreview: previewOf(incoming) || row.lastMessagePreview,
      lastMessageType: incoming.messageType || row.lastMessageType,
    };
  });
}

/** IM conversation preview is not refreshed on revoke; do not invent unread/order/at. */
export function applyMessageRevokedToInbox(conversations, message) {
  const incoming = normalizeMessage(message);
  if (!incoming.conversationId || isCustomerServiceConversation(incoming.conversationType)) {
    return conversations;
  }
  return conversations;
}

export function applyRealtimeToInbox(conversations, message, myMemberId) {
  const incoming = normalizeMessage(message);
  if (!incoming.conversationId || isCustomerServiceConversation(incoming.conversationType)) {
    return conversations;
  }
  const mine = asId(myMemberId);
  return conversations.map((row) => {
    if (row.conversationId !== incoming.conversationId) return row;
    const seq = incoming.seq;
    const myLastReadSeq = Number(row.myLastReadSeq) || 0;
    const fromOther = incoming.senderMemberId && incoming.senderMemberId !== mine;
    return {
      ...row,
      lastMessagePreview: previewOf(incoming) || row.lastMessagePreview,
      lastMessageAt: incoming.createdAt || row.lastMessageAt,
      lastMessageSeq: seq == null ? row.lastMessageSeq : seq,
      lastMessageType: incoming.messageType || row.lastMessageType,
      lastMessageSenderId: incoming.senderMemberId,
      unreadCount: fromOther && seq != null ? Math.max(0, seq - myLastReadSeq) : row.unreadCount,
    };
  });
}

export function conversationTitle(conversation, summaries = {}, groupTitles = {}) {
  if (conversation?.conversationType === DIRECT) {
    const peer = summaries[conversation.peerMemberId];
    if (peer) {
      return travelMemberProductDisplayName(peer, { genericFallback: '對話' });
    }
    return conversation.peerMemberId
      ? travelMemberProductDisplayName({}, { genericFallback: '對話' })
      : '對話';
  }
  if (conversation?.conversationType === 'BUSINESS_GROUP' && conversation.businessSystem === 'TRAVEL'
    && conversation.businessType === 'GROUP_TRIP' && groupTitles[String(conversation.businessId)]) return groupTitles[String(conversation.businessId)];
  return conversation?.title || (conversation?.conversationType === 'BUSINESS_GROUP' ? '旅程聊天室' : '群組對話');
}

export function summaryIdsFromConversations(list) {
  const ids = [];
  (list || []).forEach((row) => {
    if (row.peerMemberId) ids.push(row.peerMemberId);
    (row.participantMemberIds || []).forEach((id) => ids.push(id));
  });
  return [...new Set(ids.filter(Boolean))];
}

export function indexSummaries(rows) {
  const summaries = {};
  (rows || []).forEach((row) => {
    const id = asId(row?.memberId);
    if (id) summaries[id] = { ...row, memberId: id };
  });
  return summaries;
}


export const CHAT_READ_CONFIRMED = 'travel:chat-read-confirmed';

// 只套用本人的單調已讀游標；保留回覆後新進訊息，其他對話不受影響。
export function applyConfirmedRead(rows, receipt, memberId) {
  if (asId(receipt?.memberId) !== asId(memberId)) return rows;
  const seq = Number(receipt?.lastReadSeq);
  if (!Number.isSafeInteger(seq) || seq < 0) return rows;
  return rows.map(row => {
    if (row.conversationId !== asId(receipt.conversationId)) return row;
    const read = Math.max(Number(row.myLastReadSeq) || 0, seq);
    const last = Math.max(Number(row.lastMessageSeq) || 0, Number(receipt.lastMessageSeq) || 0);
    return { ...row, myLastReadSeq: read, lastMessageSeq: last, unreadCount: Math.max(0, last - read) };
  });
}

export function chatDate(value, locale, timeZone) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const options = timeZone ? { timeZone } : {};
  const key = new Intl.DateTimeFormat('en-CA', { ...options, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  return { key, label: new Intl.DateTimeFormat(locale, { ...options, year: 'numeric', month: 'long', day: 'numeric' }).format(date) };
}
